import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  CreateUserErrorReason,
  CreateUserRequest
} from "@dealer-center/ddc-pages-configuration-interface";
import {
  type ConfigurationWriteRequestError,
  configurationWriteRequest
} from "./configurationRequest";
import { addUserToListCache, seedCreatedUserInfo } from "./userCacheWrites";

export interface CreateUserVariables {
  accountId: string;
  userId: string;
  firstName: string;
  lastName: string;
  password: string;
  /** Copies the dealership's company, phone and address onto the user. */
  useDealershipInfo: boolean;
}

/**
 * Password hygiene: no `mutationKey` (tooling reads it), `gcTime: 0` so the settled `variables`
 * are dropped, and nothing is logged or stored.
 *
 * On success the cache is written rather than refetched: CC9 reads lag writes, so an immediate
 * refetch would 404 for the new user.
 */
export const useCreateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    ConfigurationWriteRequestError<CreateUserErrorReason>,
    CreateUserVariables
  >({
    mutationFn: ({
      accountId,
      userId,
      firstName,
      lastName,
      password,
      useDealershipInfo
    }) => {
      const body: CreateUserRequest = {
        firstName,
        lastName,
        password,
        useDealershipInfo
      };

      return configurationWriteRequest<CreateUserErrorReason>(
        { resource: "user", userId, accountId },
        body
      );
    },
    onSuccess: (_data, { accountId, userId, firstName, lastName }) => {
      seedCreatedUserInfo(queryClient, accountId, {
        userId,
        firstName,
        lastName
      });
      return addUserToListCache(queryClient, accountId, {
        userId,
        firstName,
        lastName,
        email: "",
        picture: "",
        lastSignIn: null,
        lastLogin: null
      });
    },
    gcTime: 0
  });
};
